import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkle, CircleNotch, Eye, EyeSlash } from "@phosphor-icons/react";

export default function AuthPage({ mode = "login" }) {
  const { login, register, user, error } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  if (user && user !== false && user !== null) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = mode === "login"
      ? await login(form.email, form.password)
      : await register(form.name, form.email, form.password);
    setBusy(false);
    if (ok) nav("/app");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden" style={{ background: "var(--bg-soft)" }}>
        <div className="blob-warm" style={{ width: 480, height: 480, top: -120, right: -140 }} />
        <div className="blob-warm" style={{ width: 320, height: 320, bottom: -60, left: -100, opacity: 0.4 }} />

        <Link to="/" className="relative flex items-center gap-2" data-testid="auth-brand">
          <span className="inline-block w-7 h-7 rounded-full" style={{ background: "var(--grad-warm)" }} />
          <span className="text-lg font-semibold tracking-tight">BrandLoop</span>
        </Link>

        <div className="relative">
          <div className="chip chip-accent inline-flex mb-6"><Sparkle size={12} weight="fill"/> Personal brand manager</div>
          <h2 className="text-5xl font-semibold tracking-tight leading-[1.02] max-w-lg">
            Your personal brand just made its <span className="editorial grad-text">first hire.</span>
          </h2>
          <p className="mt-5 text-base max-w-md" style={{ color: "var(--ink-2)" }}>
            Not another AI writer. A brand manager who learns your voice, your stories, your goals. The whole you. And gets better at being yours every week.
          </p>
        </div>

        <div className="relative flex flex-wrap gap-2">
          <span className="chip">5 drafts free</span>
          <span className="chip">No credit card</span>
          <span className="chip">Cancel anytime</span>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="auth-form">
          <div className="eyebrow mb-3">{mode === "login" ? "Welcome back" : "Create your workspace"}</div>
          <h1 className="text-4xl font-semibold mb-8 tracking-tight leading-[1.05]">
            {mode === "login" ? "Sign in." : <>Start your <span className="editorial grad-text">loop.</span></>}
          </h1>

          {mode === "register" && (
            <label className="block mb-4">
              <div className="eyebrow mb-2">Name</div>
              <input required data-testid="auth-name" className="input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Billy Marketer" />
            </label>
          )}
          <label className="block mb-4">
            <div className="eyebrow mb-2">Email</div>
            <input required type="email" data-testid="auth-email" className="input" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
          </label>
          <label className="block mb-6">
            <div className="eyebrow mb-2">Password</div>
            <div className="relative">
              <input required type={showPassword ? "text" : "password"} data-testid="auth-password" className="input pr-12" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" />
              <button
                type="button"
                data-testid="auth-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                title={showPassword ? "Hide password" : "Show password"}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-2 my-auto h-9 w-9 rounded-full flex items-center justify-center hover:bg-[color:var(--paper)]"
                style={{ color: "var(--ink-2)" }}
              >
                {showPassword ? <EyeSlash size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </label>

          {error && <div data-testid="auth-error" className="mb-4 text-sm" style={{ color: "var(--accent)" }}>{String(error)}</div>}

          {mode === "login" && (
            <div className="mb-4 text-right">
              <Link to="/forgot-password" className="text-sm link-underline" style={{ color: "var(--ink-2)" }} data-testid="link-forgot">Forgot password?</Link>
            </div>
          )}

          <button disabled={busy} type="submit" data-testid="auth-submit" className="btn btn-accent w-full btn-lg mb-4">
            {busy ? <><CircleNotch size={14} className="animate-spin"/> Signing in</> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16}/></>}
          </button>

          <div className="text-sm" style={{ color: "var(--ink-3)" }}>
            {mode === "login" ? (
              <>New here? <Link to="/register" className="link-underline" style={{ color: "var(--ink)" }} data-testid="link-register">Create an account →</Link></>
            ) : (
              <>Already have one? <Link to="/login" className="link-underline" style={{ color: "var(--ink)" }} data-testid="link-login">Sign in →</Link></>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
